/*
 * Compare two sessions - and say where the difference came from.
 *
 * A Cadet 9 has no power to spare, so the lap is won by the speed it carries
 * through the corners. The chassis is read corner by corner - entry, mid-corner
 * and exit speed over every clean lap - on a track map with the corners
 * numbered, so "C11 lost 3.9 km/h mid-corner on the same entry" points at a
 * real bend. The engine verdict (straights) works across layouts; the corners
 * need the same layout.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Cpu, Gauge, Wrench } from 'lucide-react'
import { api } from '../lib/api'
import type { AdviceChange, AdviceResult, CompareResult, CornerCompare, CornerPattern, CornerRow, LapOffered, SessionSummary, User } from '../lib/api'
import { Empty, Spinner } from '../components/bits'
import { dateTime, lapTime } from '../lib/format'

function Metric({ label, a, b, delta, unit, betterWhen, help }: {
  label: string; a: number | null; b: number | null; delta: number | null
  unit?: string; betterWhen: 'higher' | 'lower'; help?: string
}) {
  const good = delta === null || delta === 0 ? 'flat'
    : (betterWhen === 'higher') === (delta > 0) ? 'up' : 'down'
  const color = good === 'flat' ? 'var(--muted)'
    : good === 'up' ? 'var(--green)' : 'var(--amber)'
  return (
    <div className="between" style={{ padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
      <div>
        <div style={{ fontSize: 13 }}>{label}</div>
        {help && <div className="tiny dim">{help}</div>}
      </div>
      <div className="row" style={{ gap: 12, alignItems: 'baseline' }}>
        <span className="dim small">{a ?? '—'}</span>
        <ArrowRight size={12} style={{ opacity: .4 }} />
        <span style={{ fontWeight: 650 }}>{b ?? '—'}{unit}</span>
        {delta !== null && delta !== 0 && (
          <span style={{ color, fontWeight: 600, minWidth: 52, textAlign: 'right' }}>
            {delta > 0 ? '+' : ''}{delta}
          </span>
        )}
      </div>
    </div>
  )
}

function ChangeRow({ c, lead }: { c: AdviceChange; lead?: boolean }) {
  return (
    <div style={{ padding: '10px 0', borderTop: lead ? 'none' : '1px solid var(--line)' }}>
      <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 650, fontSize: lead ? 16 : 14 }}>{c.label}</span>
        <span style={{ fontWeight: 650, color: 'var(--blue-2)' }}>{c.direction}</span>
        <span className="tiny dim">{c.confidence} confidence</span>
      </div>
      <div className="tiny dim" style={{ marginTop: 3 }}>{c.why}</div>
    </div>
  )
}

function AdvicePanel({ a, b, lapQs }: { a: number; b: number; lapQs: string }) {
  // Deliberately not automatic. It is a paid call, and at a track the mechanic
  // asks for it when he is about to change something - not every time a
  // dropdown moves.
  const [asked, setAsked] = useState(false)
  const q = useQuery({
    queryKey: ['advice', a, b, lapQs],
    queryFn: () => api.get<AdviceResult>(`/api/analysis/compare/advice?a=${a}&b=${b}${lapQs}`),
    enabled: asked,
    staleTime: Infinity,
    retry: false,
  })

  if (!asked) {
    return (
      <div className="card" style={{ marginTop: 14 }}>
        <button className="btn" onClick={() => setAsked(true)}>
          <Wrench size={14} /> What should we change?
        </button>
        <div className="tiny dim" style={{ marginTop: 6 }}>
          Reads these numbers and the setups behind them, and gives one change to make first.
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-head"><h2 className="row" style={{ gap: 8 }}><Wrench size={17} /> Setup direction</h2></div>
      {q.isPending && <Spinner />}
      {q.isError && (
        <p className="tiny" style={{ color: 'var(--amber)' }}>
          Could not get advice — {(q.error as Error).message}. The numbers above are unaffected.
        </p>
      )}
      {q.data && (() => {
        const v = q.data.advice
        return (
          <>
            <p style={{ fontWeight: 650, marginTop: 0 }}>{v.headline}</p>
            <p className="dim" style={{ fontSize: 14 }}>{v.read}</p>

            {v.first_change ? (
              <>
                <div className="tiny dim" style={{ textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 14 }}>
                  Change this first
                </div>
                <ChangeRow c={v.first_change} lead />
              </>
            ) : (
              <p className="tiny" style={{ color: 'var(--amber)' }}>
                No change recommended off this comparison.
              </p>
            )}

            {v.then.length > 0 && (
              <>
                <div className="tiny dim" style={{ textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 14 }}>
                  Only if that does not settle it
                </div>
                {v.then.map((c, i) => <ChangeRow key={i} c={c} />)}
              </>
            )}

            {v.watch.length > 0 && (
              <>
                <div className="tiny dim" style={{ textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 14 }}>
                  Then check
                </div>
                <ul className="tiny" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {v.watch.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </>
            )}

            {v.cautions.length > 0 && (
              <ul className="tiny" style={{ margin: '14px 0 0', paddingLeft: 18, color: 'var(--amber)' }}>
                {v.cautions.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            )}

            {(!q.data.setup_recorded.a || !q.data.setup_recorded.b) && (
              <p className="tiny dim" style={{ marginTop: 12, marginBottom: 0 }}>
                No setup was recorded for {!q.data.setup_recorded.a ? 'the baseline' : 'the compared'} session,
                so this reads the telemetry alone.
              </p>
            )}
          </>
        )
      })()}
    </div>
  )
}

function layoutLabel(side: { layout_name: string | null; layout_direction: string | null }) {
  if (!side.layout_name) return 'layout not recorded'
  return side.layout_direction ? `${side.layout_name} (${side.layout_direction})` : side.layout_name
}

const PATTERN_COLOUR: Record<CornerPattern, string> = {
  mid_lost: 'var(--amber)',
  entry_slower: 'var(--blue-2)',
  exit_slower: 'var(--red)',
  quicker: 'var(--green)',
}
const PATTERN_SHORT: Record<CornerPattern, string> = {
  mid_lost: 'same entry, mid-corner lost',
  entry_slower: 'slower in',
  exit_slower: 'slower out',
  quicker: 'carrying more',
}

function LapPicker({ side, run, laps, picked, onChange }: {
  side: string; run: number; laps: LapOffered[]; picked: number[] | null
  onChange: (laps: number[] | null) => void
}) {
  // What is on screen now: the hand pick, or what the server used by default.
  const current = picked ?? laps.filter((l) => l.used).map((l) => l.lap_number)
  const fastest = laps.length ? Math.min(...laps.map((l) => l.duration_s)) : null
  const toggle = (n: number) =>
    onChange(current.includes(n) ? current.filter((x) => x !== n) : [...current, n].sort((x, y) => x - y))
  return (
    <div className="row wrap" style={{ gap: 6, alignItems: 'center', marginBottom: 8 }}>
      <span className="small" style={{ minWidth: 92 }}><b>{side}</b> <span className="dim">run {run}</span></span>
      {laps.map((l) => {
        const on = current.includes(l.lap_number)
        return (
          <button key={l.lap_number} onClick={() => toggle(l.lap_number)}
            title={l.clean ? 'clean lap' : 'not counted as clean by default (slow lap)'}
            style={{
              padding: '3px 8px', borderRadius: 'var(--r-pill)', fontSize: 12, cursor: 'pointer',
              border: `1px solid ${on ? 'var(--blue-2)' : 'var(--line-strong)'}`,
              background: on ? 'rgba(58,160,255,.15)' : 'transparent',
              color: on ? 'var(--ink)' : 'var(--ink-3)',
              fontVariantNumeric: 'tabular-nums',
            }}>
            {l.label} · {l.duration_s.toFixed(2)}{l.duration_s === fastest ? ' ★' : ''}
          </button>
        )
      })}
      <button className="btn ghost" style={{ padding: '3px 8px', fontSize: 12 }}
        onClick={() => onChange(null)} disabled={picked === null}>Clean laps</button>
      <button className="btn ghost" style={{ padding: '3px 8px', fontSize: 12 }}
        onClick={() => onChange([])} disabled={picked !== null && picked.length === 0}>Clear</button>
    </div>
  )
}

function TrackMap({ c, hover, onHover }: {
  c: CornerCompare; hover: number | null; onHover: (n: number | null) => void
}) {
  const pts = c.map.points
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1])
  const pad = 14
  const x0 = Math.min(...xs) - pad, x1 = Math.max(...xs) + pad
  const y0 = Math.min(...ys) - pad, y1 = Math.max(...ys) + pad
  // North up: SVG y runs down, so every y is negated.
  const line = (seg: [number, number][]) =>
    seg.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${(-p[1]).toFixed(1)}`).join(' ')
  const byCorner = new Map(c.corners.map((r) => [r.corner, r]))
  const [sx, sy] = pts[0]
  const [ax, ay] = pts[Math.min(4, pts.length - 1)]
  const ang = Math.atan2(-(ay - sy), ax - sx) * 180 / Math.PI
  return (
    <svg viewBox={`${x0} ${-y1} ${x1 - x0} ${y1 - y0}`} style={{ width: '100%', height: 'auto' }}>
      <path d={line(pts) + ' Z'} fill="none" stroke="var(--line-strong)" strokeWidth={7}
        strokeLinejoin="round" strokeLinecap="round" />
      {c.map.corners.map((m) => {
        const row = byCorner.get(m.corner)
        const colour = row?.pattern ? PATTERN_COLOUR[row.pattern] : 'var(--ink-3)'
        return (
          <path key={m.corner} d={line(pts.slice(m.from, m.to + 1))} fill="none" stroke={colour}
            strokeWidth={hover === m.corner ? 11 : 7} strokeLinecap="round" />
        )
      })}
      <g transform={`translate(${sx},${-sy}) rotate(${ang})`}>
        <line x1={0} y1={-9} x2={0} y2={9} stroke="var(--ink)" strokeWidth={2.5} />
        <path d="M6,-5 L14,0 L6,5 Z" fill="var(--ink)" />
      </g>
      {c.map.corners.map((m) => {
        const row = byCorner.get(m.corner)
        const colour = row?.pattern ? PATTERN_COLOUR[row.pattern] : 'var(--ink-3)'
        const on = hover === m.corner
        return (
          <g key={m.corner} transform={`translate(${m.label[0]},${-m.label[1]})`}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => onHover(m.corner)} onMouseLeave={() => onHover(null)}>
            <circle r={on ? 10 : 8} fill="var(--card)" stroke={colour} strokeWidth={2} />
            <text y={3.5} textAnchor="middle" fontSize={on ? 11 : 9.5} fontWeight={700}
              fill="var(--ink)">{m.corner}</text>
          </g>
        )
      })}
    </svg>
  )
}

function Delta({ v, called, unit = '', lowerIsBetter = false }: {
  v: number; called: boolean; unit?: string; lowerIsBetter?: boolean
}) {
  const worse = lowerIsBetter ? v > 0 : v < 0
  const colour = !called ? 'var(--ink-3)' : worse ? 'var(--amber)' : 'var(--green)'
  return <span style={{ color: colour, fontWeight: called ? 700 : 400 }}>{v > 0 ? '+' : ''}{v}{unit}</span>
}

function CornerTable({ c, hover, onHover }: {
  c: CornerCompare; hover: number | null; onHover: (n: number | null) => void
}) {
  const cell = (r: CornerRow, k: 'entry' | 'mid' | 'exit') => (
    <td className="num small">
      <span className="dim">{r.a[k]} → {r.b[k]}</span>{' '}
      <Delta v={r.delta[k]} called={r.called[k]} />
    </td>
  )
  return (
    <div className="scroll-x">
      <table>
        <thead><tr><th>Corner</th><th className="num">Entry</th><th className="num">Mid-corner</th>
          <th className="num">Exit</th><th className="num">Time</th><th>Reading</th></tr></thead>
        <tbody>
          {c.corners.map((r) => (
            <tr key={r.corner} onMouseEnter={() => onHover(r.corner)} onMouseLeave={() => onHover(null)}
              style={{ background: hover === r.corner ? 'var(--card-2)' : undefined }}>
              <td><b>C{r.corner}</b> <span className="tiny dim">{r.at_pct}%</span></td>
              {cell(r, 'entry')}{cell(r, 'mid')}{cell(r, 'exit')}
              <td className="num small"><Delta v={r.delta.time_s} called={r.called.time} unit="s" lowerIsBetter /></td>
              <td className="small" title={r.pattern_text ?? undefined}
                style={{ color: r.pattern ? PATTERN_COLOUR[r.pattern] : 'var(--ink-3)' }}>
                {r.pattern ? PATTERN_SHORT[r.pattern] : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TraceOverlay({ a, b }: { a: { d: number; kmh: number }[]; b: { d: number; kmh: number }[] }) {
  const W = 720, H = 220, pad = 28
  const all = [...a, ...b]
  const maxD = Math.max(...all.map((p) => p.d), 1)
  const minV = Math.min(...all.map((p) => p.kmh))
  const maxV = Math.max(...all.map((p) => p.kmh))
  const x = (d: number) => pad + (d / maxD) * (W - 2 * pad)
  const y = (v: number) => H - pad - ((v - minV) / (maxV - minV || 1)) * (H - 2 * pad)
  const path = (pts: { d: number; kmh: number }[]) =>
    pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.d).toFixed(1)},${y(p.kmh).toFixed(1)}`).join(' ')
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      <path d={path(a)} fill="none" stroke="var(--muted)" strokeWidth={1.6} opacity={0.8} />
      <path d={path(b)} fill="none" stroke="var(--blue-2)" strokeWidth={2} />
      <text x={pad} y={14} className="tiny" fill="var(--muted)">km/h vs distance · grey = A, blue = B</text>
    </svg>
  )
}

function SidePicker({ label, byTrack, tracks, sessionId, onPick, labelOf }: {
  label: string
  byTrack: Map<string, SessionSummary[]>
  tracks: string[]
  sessionId: number | null
  onPick: (id: number) => void
  labelOf: (s: SessionSummary) => string
}) {
  // Which track this session belongs to, so the second dropdown starts right.
  const currentTrack = useMemo(() => {
    for (const [name, list] of byTrack)
      if (list.some((s) => s.id === sessionId)) return name
    return tracks[0] ?? null
  }, [byTrack, tracks, sessionId])
  const [track, setTrack] = useState<string | null>(null)
  const t = track ?? currentTrack
  const list = (t && byTrack.get(t)) || []

  return (
    <div>
      <label className="tiny dim">{label}</label>
      <select value={t ?? ''} style={{ width: '100%', marginBottom: 8 }}
        onChange={(e) => {
          const name = e.target.value
          setTrack(name)
          const first = byTrack.get(name)?.[0]
          if (first) onPick(first.id)
        }}>
        {tracks.map((name) => (
          <option key={name} value={name}>{name} ({byTrack.get(name)?.length})</option>
        ))}
      </select>
      <select value={sessionId ?? ''} style={{ width: '100%' }}
        onChange={(e) => onPick(Number(e.target.value))}>
        {list.map((s) => (
          <option key={s.id} value={s.id}>
            run {s.run_id} · {labelOf(s).split('·').slice(2).join('·').trim()}
          </option>
        ))}
      </select>
    </div>
  )
}


export function Compare({ user }: { user: User }) {
  const list = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<{ sessions: SessionSummary[] }>('/api/sessions/compare-options'),
  })
  const sessions = useMemo(
    () => (list.data?.sessions ?? [])
      .filter((s) => s.telemetry_complete && s.best_lap_s !== null),
    [list.data])

  const byTrack = useMemo(() => {
    const m = new Map<string, SessionSummary[]>()
    for (const s of sessions) {
      const key = s.track?.name ?? 'Other'
      ;(m.get(key) ?? m.set(key, []).get(key)!).push(s)
    }
    return m
  }, [sessions])
  const trackNames = useMemo(() => [...byTrack.keys()], [byTrack])

  const [aId, setA] = useState<number | null>(null)
  const [bId, setB] = useState<number | null>(null)

  // Default to the two most recent sessions with telemetry.
  const defaults = useMemo(() => sessions.slice(0, 2).map((s) => s.id), [sessions])
  const a = aId ?? defaults[1] ?? null   // baseline = older of the two
  const b = bId ?? defaults[0] ?? null   // judged = newer

  // Hand-picked laps per side, tied to the session they were picked on so a
  // new session starts back on its clean laps. null = the clean laps.
  const [pickA, setPickA] = useState<{ id: number; laps: number[] } | null>(null)
  const [pickB, setPickB] = useState<{ id: number; laps: number[] } | null>(null)
  const lapsA = pickA && pickA.id === a ? pickA.laps : null
  const lapsB = pickB && pickB.id === b ? pickB.laps : null
  const lapQs = (lapsA?.length ? `&laps_a=${lapsA.join(',')}` : '')
    + (lapsB?.length ? `&laps_b=${lapsB.join(',')}` : '')
  const emptyPick = (lapsA !== null && lapsA.length === 0) || (lapsB !== null && lapsB.length === 0)

  const cmp = useQuery({
    queryKey: ['compare', a, b, lapQs],
    queryFn: () => api.get<CompareResult>(`/api/analysis/compare?a=${a}&b=${b}${lapQs}`),
    enabled: a !== null && b !== null && a !== b && !emptyPick,
    // Keep the last result on screen while a lap toggle refetches, so the
    // page does not blank out on every click.
    placeholderData: (prev) => prev,
  })
  const onLaps = (side: 'a' | 'b', laps: number[] | null) => {
    const id = side === 'a' ? a : b
    if (id === null) return
    ;(side === 'a' ? setPickA : setPickB)(laps === null ? null : { id, laps })
  }

  const label = (s: SessionSummary) =>
    `${s.track?.name ?? '?'} · run ${s.run_id} · ${lapTime(s.best_lap_s)}` +
    ` · ${dateTime(s.started_at, s.timezone).split(',')[0]}`

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Compare</h1>
          <div className="sub">Two sessions, and where the difference came from</div>
        </div>
      </div>

      {list.isLoading ? <Spinner /> : sessions.length < 2 ? (
        <Empty title="Need two sessions with telemetry"
          body="Once two sessions have their GPS uploaded, pick them here to see what changed and what it did." />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="grid" style={{ gap: 12, gridTemplateColumns: '1fr auto 1fr', alignItems: 'start' }}>
              <SidePicker label="Baseline (A)" byTrack={byTrack} tracks={trackNames}
                sessionId={a} onPick={setA} labelOf={label} />
              <ArrowRight size={18} style={{ opacity: .5, marginTop: 28 }} />
              <SidePicker label="Compared (B)" byTrack={byTrack} tracks={trackNames}
                sessionId={b} onPick={setB} labelOf={label} />
            </div>
          </div>

          {a === b ? (
            <Empty title="Pick two different sessions" body="A and B are the same." />
          ) : cmp.isLoading ? <Spinner />
          : cmp.isError ? <Empty title="Couldn't compare these"
              body="Both sessions need telemetry uploaded." />
          : cmp.data && <CompareBody r={cmp.data} owner={user.is_owner} lapQs={lapQs}
              lapsA={lapsA} lapsB={lapsB} onLaps={onLaps} emptyPick={emptyPick} />}
        </>
      )}
    </div>
  )
}

function CompareBody({ r, owner, lapQs, lapsA, lapsB, onLaps, emptyPick }: {
  r: CompareResult; owner: boolean; lapQs: string
  lapsA: number[] | null; lapsB: number[] | null
  onLaps: (side: 'a' | 'b', laps: number[] | null) => void; emptyPick: boolean
}) {
  const faster = r.lap_delta_s !== null && r.lap_delta_s < 0
  const [hover, setHover] = useState<number | null>(null)
  const qc = useQueryClient()
  // The numbering is stored per layout so C11 stays C11. If it was first built
  // from a session that merged two bends, the owner renumbers it from a clean one.
  const renumber = useMutation({
    mutationFn: () => api.post(`/api/analysis/corner-map?session_id=${r.a.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compare'] }),
  })
  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="between wrap" style={{ gap: 12 }}>
          <div>
            <div className="tiny dim">Best lap</div>
            <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
              <span className="dim">{lapTime(r.a.best_lap_s)}</span>
              <ArrowRight size={14} style={{ opacity: .4 }} />
              <span className="lap best" style={{ fontSize: 24 }}>{lapTime(r.b.best_lap_s)}</span>
              {r.lap_delta_s !== null && (
                <span style={{ fontWeight: 700, color: faster ? 'var(--green)' : 'var(--amber)' }}>
                  {r.lap_delta_s > 0 ? '+' : ''}{r.lap_delta_s}s
                </span>
              )}
            </div>
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            <span className={`badge ${
              r.engine.verdict === 'stronger' ? 'saved'
                : r.engine.verdict === 'weaker' ? 'lost' : 'none'}`}>
              <Cpu size={12} /> Engine {r.engine.verdict}
            </span>
            <span className={`badge ${r.chassis.b_better ? 'saved' : 'none'}`}>
              <Gauge size={12} /> Chassis {r.chassis.verdict}
            </span>
          </div>
          {r.chassis.note && (
            <p className="tiny" style={{ marginTop: 8, marginBottom: 0, color: 'var(--amber)' }}>
              {r.chassis.note}
            </p>
          )}
          {r.engine.note && (
            <p className="tiny" style={{ marginTop: 8, marginBottom: 0, color: 'var(--amber)' }}>
              {r.engine.note}
            </p>
          )}
        </div>
        {!r.same_layout && (
          <p className="tiny dim" style={{ marginTop: 10, marginBottom: 0 }}>
            {r.a.layout_name && r.b.layout_name
              ? `Different layout — ${layoutLabel(r.a)} vs ${layoutLabel(r.b)}. `
              : 'Layout not recorded on one of these sessions, so they cannot be lined up. '}
            Corner speeds and the speed trace need the same circuit; the engine numbers compare fine
            across layouts.
          </p>
        )}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <h2>Laps</h2>
          <span className="tiny dim">tap a lap to add or drop it · drop tow laps, or clear and pick one</span>
        </div>
        <LapPicker side="A" run={r.a.run_id} laps={r.laps.a} picked={lapsA}
          onChange={(l) => onLaps('a', l)} />
        <LapPicker side="B" run={r.b.run_id} laps={r.laps.b} picked={lapsB}
          onChange={(l) => onLaps('b', l)} />
        {emptyPick && (
          <p className="tiny" style={{ color: 'var(--amber)', marginBottom: 0 }}>
            Pick at least one lap on each side — the numbers below are from before.
          </p>
        )}
      </div>

      <div className="grid" style={{ gap: 14, gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))' }}>
        <div className="card">
          <div className="card-head"><h2 className="row" style={{ gap: 8 }}><Cpu size={17} /> Engine — the straights</h2></div>
          <Metric label="Top speed" a={r.engine.a.top_speed_kmh} b={r.engine.b.top_speed_kmh}
            delta={r.engine.deltas.top_speed_kmh} unit=" km/h" betterWhen="higher" />
          <Metric label="Peak RPM" a={r.engine.a.peak_rpm} b={r.engine.b.peak_rpm}
            delta={r.engine.deltas.peak_rpm} betterWhen="higher" />
          <Metric label="Straight pull" a={r.engine.a.straight_pull_kmh_s} b={r.engine.b.straight_pull_kmh_s}
            delta={r.engine.deltas.straight_pull_kmh_s} unit=" km/h/s" betterWhen="higher"
            help="how hard it accelerates on the straights" />
        </div>

        {r.corners && (
          <div className="card">
            <div className="card-head"><h2 className="row" style={{ gap: 8 }}><Gauge size={17} /> Corners</h2></div>
            <TrackMap c={r.corners} hover={hover} onHover={setHover} />
            <div className="between wrap" style={{ gap: 8, marginTop: 6 }}>
              <span className="tiny dim">
                {r.corners.numbered_from_run ? `Corners numbered from run ${r.corners.numbered_from_run}` : ''}
              </span>
              {owner && r.corners.numbered_from_run !== r.a.run_id && (
                <button className="btn small" disabled={renumber.isPending}
                  onClick={() => renumber.mutate()}>
                  {renumber.isPending ? 'Renumbering…' : `Number corners from run ${r.a.run_id}`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {r.corners && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-head">
            <h2>Corner by corner</h2>
            <span className="tiny dim">
              {r.laps.picked_a || r.laps.picked_b ? 'picked laps: ' : 'median of clean laps: '}
              {r.corners.laps_a} (A) vs {r.corners.laps_b} (B) · corners{' '}
              {r.corners.corner_time_delta_s > 0 ? '+' : ''}{r.corners.corner_time_delta_s}s
            </span>
          </div>
          <CornerTable c={r.corners} hover={hover} onHover={setHover} />
          <p className="tiny dim" style={{ marginTop: 10, marginBottom: 0 }}>
            Speeds in km/h, B minus A. A difference is only coloured when it is at least 1 km/h and
            bigger than the lap-to-lap spread. Same entry with mid-corner speed lost is the kart
            gripping up — or short of grip; the conditions and the video say which.
            {(r.corners.laps_a === 1 || r.corners.laps_b === 1) &&
              ' With a single lap on a side there is no spread to test against, so only the 1 km/h floor applies — one lap is one lap.'}
          </p>
        </div>
      )}

      <AdvicePanel key={lapQs} a={r.a.id} b={r.b.id} lapQs={lapQs} />

      {r.traces && r.traces.a.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-head"><h2>Speed trace</h2></div>
          <TraceOverlay a={r.traces.a} b={r.traces.b} />
        </div>
      )}

    </>
  )
}
